import { ApiError, apiFetch } from "../api/client";
import { openSession } from "./session";

/**
 * ═══ LA CONNEXION PAR CODE ═══
 *
 * Deux appels, tous deux PUBLICS et sur APIX (`api/src/routes/otp/`) : on demande un
 * code à six chiffres pour une adresse, il arrive par email, on le présente. Le
 * serveur rend alors un jeton de 90 jours, le même que celui d'une connexion par mot de
 * passe, et la session s'ouvre par `openSession`.
 *
 * ⚠️ LE SERVEUR CRÉE LE COMPTE S'IL N'EXISTE PAS. `/otp/send` pose `Authentications` et
 * `Users` (du domaine) dès la demande, avant même que le code soit prouvé : quelqu'un qui
 * n'a jamais eu de compte se connecte donc au premier code, sans inscription préalable.
 * C'est voulu côté API (c'est ce qui permet une billetterie sans compte), et c'est pour
 * cela qu'on n'affiche jamais « adresse inconnue » : il n'y a pas d'adresse inconnue.
 *
 * ⚠️ ET LE CODE N'EST PAS LIÉ À L'ÉVÉNEMENT, seulement à l'adresse : un seul code vit à
 * la fois par adresse, une nouvelle demande remplace la précédente. Cinq essais, cinq
 * minutes. Passé l'un ou l'autre, la seule issue est de redemander un code, et c'est ce
 * que l'écran propose quand le serveur le dit.
 */

/** L'email est parti (ou pas : l'événement n'a aucun modèle pour ce code). */
export type OtpRequested = { sent: boolean };

/** Ce que le serveur dit d'un code refusé, ramené à ce que l'écran peut en faire. */
export type OtpRefusal =
  /** Faux code, il reste des essais : on garde la case et on laisse retaper. */
  | "wrong"
  /** Périmé, trop d'essais, ou jamais demandé : il faut un nouveau code. */
  | "expired"
  /** Le service ne répond pas comme prévu : ce n'est pas la faute du code. */
  | "unavailable";

export class OtpError extends Error {
  constructor(readonly refusal: OtpRefusal) {
    super(refusal);
    this.name = "OtpError";
  }
}

/** Ce que `/otp/send` répond quand aucun modèle d'e-mail n'est branché sur l'événement. */
const NO_TEMPLATE = /template not configured/i;

export async function requestOtp(
  domainId: number,
  eventId: number,
  email: string,
): Promise<OtpRequested> {
  try {
    await apiFetch<{ ok?: boolean }>("/otp/send", {
      api: "apix",
      method: "POST",
      // Même règle que `signIn` : on tape "  Jean@Ex.FR " et le code doit arriver.
      body: { email: normalizeEmail(email), domainId, eventId },
    });
    return { sent: true };
  } catch (cause) {
    /**
     * ⚠️ ON NE PROMET PAS UN EMAIL QUI NE PARTIRA JAMAIS. Le serveur ne dit pas
     * `skipped` comme l'envoi v3 : il refuse, avec cette phrase. On la reconnaît pour
     * dire la vérité à l'écran (« cet événement n'envoie pas de code »), le reste est
     * une panne comme une autre.
     */
    if (cause instanceof ApiError && NO_TEMPLATE.test(cause.message))
      return { sent: false };
    throw cause;
  }
}

export async function verifyOtp(
  domainId: number,
  email: string,
  code: string,
): Promise<void> {
  let answer: { token?: string } | null;
  try {
    answer = await apiFetch<{ token?: string } | null>("/otp/verify", {
      api: "apix",
      method: "POST",
      body: { email: normalizeEmail(email), code: code.trim(), domainId },
    });
  } catch (cause) {
    throw new OtpError(refusalOf(cause));
  }
  if (!answer?.token) throw new OtpError("unavailable");
  openSession(answer.token);
}

/**
 * Sortie et exportée pour être testée seule : LA RÈGLE, c'est que l'écran ne répète
 * jamais la phrase du serveur (elle est en français, non traduite, et tutoie), et qu'il
 * distingue seulement « retapez » de « redemandez ».
 *
 * Les phrases sont celles de `api/src/routes/otp/verify/index.js`, toutes en 400 :
 * « Code invalide » (faux, essais restants), « Code invalide ou expire » (aucun code en
 * cours, ou périmé), « Trop de tentatives, demande un nouveau code », « Compte
 * introuvable, redemande un code ».
 */
export function refusalOf(cause: unknown): OtpRefusal {
  if (!(cause instanceof ApiError)) return "unavailable";
  if (cause.status !== 400) return "unavailable";
  const m = cause.message;
  if (/expire|tentatives|redemande|introuvable/i.test(m)) return "expired";
  if (/invalide/i.test(m)) return "wrong";
  return "unavailable";
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Six chiffres, ni plus ni moins : ce que le serveur accepte (`CODE_REGEX`). */
export const OTP_CODE_LENGTH = 6;
export function isOtpCodeShape(code: string): boolean {
  return /^\d{6}$/.test(code.trim());
}
